import React from "react";
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

const VARIANT_TYPE_OPTIONS = [
  "Talla",
  "Color",
  "Peso",
  "Capacidad",
  "Material",
  "Presentación",
  "Longitud",
  "Resistencia",
  "Nivel",
  "Sabor",
  "Fragancia",
  "Modelo",
];

const calcVariantFinalPrice = (price, iva) => {
  const numericPrice = Number(price || 0);
  const numericIva = Number(iva || 0);

  if (!numericPrice || numericIva === 999) return numericPrice.toFixed(2);

  const total = numericPrice + (numericPrice * numericIva) / 100;
  return total.toFixed(2);
};

export default function ProductVariantsEditor({
  variants,
  selectedVariantType,
  defaultIva,
  onAdd,
  onUpdate,
  onRemove,
}) {
  return (
    <Paper
      sx={{
        width: "100%",
        borderRadius: "18px",
        border: "1px solid rgba(148,163,184,0.2)",
        boxShadow: "0 16px 36px rgba(15,23,42,0.05)",
        p: { xs: 2, md: 2.4 },
        bgcolor: "#ffffff",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={1.2}
        >
          <Box>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: { xs: 16, md: 17.5 },
                fontWeight: 800,
              }}
            >
              Variantes
            </Typography>

            <Typography
              sx={{
                color: "#64748b",
                fontSize: 13,
                mt: 0.4,
              }}
            >
              Agrega tallas, colores, pesos u otras variaciones del mismo tipo.
            </Typography>
          </Box>

          <Button
            onClick={onAdd}
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Añadir variante
          </Button>
        </Stack>

        {!variants.length ? (
          <Typography
            sx={{
              color: "#64748b",
              fontSize: 13.2,
              fontWeight: 500,
            }}
          >
            Aún no has agregado variantes.
          </Typography>
        ) : (
          <Stack spacing={1.6}>
            {variants.map((item, index) => {
              const key = item.uuid || item.local_id;
              const finalPrice = calcVariantFinalPrice(item.price, item.iva_percentage);

              return (
                <Paper
                  key={key}
                  sx={{
                    borderRadius: "14px",
                    p: 1.6,
                    bgcolor: "#f8fafc",
                    border: "1px solid rgba(148,163,184,0.18)",
                    boxShadow: "none",
                  }}
                >
                  <Stack spacing={1.4}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        sx={{
                          color: "#0f172a",
                          fontSize: 13.2,
                          fontWeight: 700,
                        }}
                      >
                        Variante {index + 1}
                      </Typography>

                      <Button
                        onClick={() => onRemove(key)}
                        variant="outlined"
                        startIcon={<DeleteOutlineRoundedIcon />}
                        sx={{
                          height: 32,
                          px: 1.2,
                          borderRadius: "9px",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 12.2,
                          color: "#dc2626",
                          borderColor: "rgba(220,38,38,0.22)",
                          "&:hover": {
                            borderColor: "rgba(220,38,38,0.32)",
                            bgcolor: "rgba(220,38,38,0.06)",
                          },
                        }}
                      >
                        Quitar
                      </Button>
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <TextField
                        select
                        label="Tipo"
                        value={item.variant_type}
                        onChange={(event) =>
                          onUpdate(key, "variant_type", event.target.value)
                        }
                        fullWidth
                        size="small"
                        disabled={Boolean(selectedVariantType && item.variant_type !== "" && item.variant_type === selectedVariantType)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: "#ffffff",
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 500,
                            color: "#64748b",
                          },
                        }}
                      >
                        {VARIANT_TYPE_OPTIONS.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}
                            disabled={Boolean(selectedVariantType && selectedVariantType !== option)}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        label="Valor"
                        value={item.variant_value}
                        onChange={(event) =>
                          onUpdate(key, "variant_value", event.target.value)
                        }
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: "#ffffff",
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 500,
                            color: "#64748b",
                          },
                        }}
                      />

                      <TextField
                        label="Precio"
                        value={item.price}
                        onChange={(event) =>
                          onUpdate(key, "price", event.target.value)
                        }
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: "#ffffff",
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 500,
                            color: "#64748b",
                          },
                        }}
                      />

                      <TextField
                        label="Precio final"
                        value={finalPrice}
                        fullWidth
                        size="small"
                        disabled
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: "#eef2ff",
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 500,
                            color: "#64748b",
                          },
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <TextField
                        label="Stock"
                        value={item.stock}
                        onChange={(event) =>
                          onUpdate(key, "stock", event.target.value)
                        }
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: "#ffffff",
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 500,
                            color: "#64748b",
                          },
                        }}
                      />

                      <TextField
                        select
                        label="IVA"
                        value={item.iva_percentage}
                        onChange={(event) =>
                          onUpdate(key, "iva_percentage", event.target.value)
                        }
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: "#ffffff",
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 500,
                            color: "#64748b",
                          },
                        }}
                      >
                        <MenuItem value={0}>0%</MenuItem>
                        <MenuItem value={5}>5%</MenuItem>
                        <MenuItem value={15}>15%</MenuItem>
                        <MenuItem value={999}>No objeto de IVA</MenuItem>
                      </TextField>
                    </Box>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(item.is_active)}
                            onChange={(event) =>
                              onUpdate(key, "is_active", event.target.checked)
                            }
                          />
                        }
                        label="Variante activa"
                      />

                      {!item.variant_type && (
                        <Typography
                          sx={{
                            color: "#64748b",
                            fontSize: 12.5,
                            fontWeight: 500,
                            alignSelf: "center",
                          }}
                        >
                          IVA predeterminado: {defaultIva === 999 ? "No objeto de IVA" : `${defaultIva}%`}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}